import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [orderInfo, setOrderInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/edible-greetings/cart');
      return;
    }

    const checkPaymentStatus = async () => {
      let attempts = 0;
      const maxAttempts = 5;

      const poll = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
          const data = await response.json();

          if (data.payment_status === 'paid') {
            setStatus('success');
            setOrderInfo(data);
            return;
          } else if (data.status === 'expired') {
            setStatus('expired');
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            setStatus('timeout');
          }
        } catch (error) {
          console.error('Error checking payment:', error);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, navigate]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full p-8 text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-spin" />
          <h2 className="text-2xl font-semibold mb-2">Processing Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your purchase. Your order has been confirmed.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-600">A confirmation email has been sent to:</p>
            <p className="font-semibold">{orderInfo?.metadata?.customer_email}</p>
          </div>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/edible-greetings/catalog')} 
              className="w-full"
            >
              Continue Shopping
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/edible-greetings/')}
              className="w-full"
            >
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full p-8 text-center">
        <XCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
        <h2 className="text-2xl font-semibold mb-2">Payment {status === 'expired' ? 'Expired' : 'Error'}</h2>
        <p className="text-gray-600 mb-6">
          {status === 'expired' 
            ? 'Your payment session has expired. Please try again.'
            : 'There was an error processing your payment. Please try again.'}
        </p>
        <Button 
          onClick={() => navigate('/edible-greetings/checkout')}
          className="w-full"
        >
          Return to Checkout
        </Button>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;
